import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
  Container,
  Box,
  Paper,
  Typography,
  Button,
  Alert,
  TextField,
  CircularProgress,
  ToggleButton,
  ToggleButtonGroup,
  GridLegacy as Grid,
  LinearProgress,
} from '@mui/material';
import {
  PhoneInTalk as PhoneInTalkIcon,
  Refresh as RefreshIcon,
  PhoneCallback as AnsweredIcon,
  KeyboardReturn as CallBackIcon,
  CallReceived as InboundIcon,
  Timer as TalkTimeIcon,
  AvTimer as AvgTalkIcon,
  Today as BusiestDayIcon,
  EmojiEvents as TrophyIcon,
  Schedule as PeakHourIcon,
} from '@mui/icons-material';
import { Bar, Line, Doughnut } from 'react-chartjs-2';
import {
  format,
  startOfWeek,
  endOfWeek,
  startOfMonth,
  endOfMonth,
  startOfYear,
  endOfYear,
  startOfDay,
  endOfDay,
  eachDayOfInterval,
  parseISO,
} from 'date-fns';
import api from '../services/api';
import { useQuery } from '@tanstack/react-query';
import '../utils/chartSetup'; // Initialize Chart.js
import '../utils/chartSetup'; // Initialize Chart.js

type RangePreset = 'today' | 'week' | 'month' | 'year' | 'custom';

interface DateRange {
  start: number;
  end: number;
}

interface DailyPoint {
  date: string;
  total: number;
  answered: number;
  missed: number;
  talkSeconds: number;
}

interface ReportData {
  range: DateRange;
  timezone: string;
  truncated: boolean;
  totals: {
    totalCalls: number;
    answeredCalls: number;
    missedCalls: number;
    answerRate: number;
    inbound: number;
    outbound: number;
    recordedCalls: number;
    totalTalkSeconds: number;
    avgTalkSeconds: number;
    avgCallsPerDay: number;
  };
  highlights: {
    longestCall: {
      duration_seconds: number;
      agent_name: string | null;
      contact_name: string | null;
      contact_phone: string | null;
      date_started: number | null;
    } | null;
    busiestDay: DailyPoint | null;
    peakHour: { hour: number; total: number; answered: number } | null;
  };
  daily: DailyPoint[];
  hourly: { hour: number; total: number; answered: number }[];
  byDayOfWeek: { day: string; total: number; answered: number; missed: number; talkSeconds: number }[];
}

const formatDuration = (seconds?: number | null): string => {
  const total = Number(seconds);
  if (!Number.isFinite(total) || total <= 0) return '0:00';
  const hours = Math.floor(total / 3600);
  const mins = Math.floor((total % 3600) / 60);
  const secs = Math.round(total % 60);
  if (hours > 0) {
    return `${hours}h ${mins.toString().padStart(2, '0')}m`;
  }
  return `${mins}:${secs.toString().padStart(2, '0')}`;
};

const formatTalkHours = (seconds: number): string => {
  if (seconds < 3600) return `${Math.round(seconds / 60)}m`;
  const hours = seconds / 3600;
  return `${hours >= 10 ? Math.round(hours) : hours.toFixed(1)}h`;
};

const formatHour = (hour: number): string => {
  if (hour === 0) return '12 AM';
  if (hour < 12) return `${hour} AM`;
  if (hour === 12) return '12 PM';
  return `${hour - 12} PM`;
};

const computeRange = (preset: RangePreset, customStart: string, customEnd: string): DateRange => {
  const now = new Date();
  let start: Date;
  let end: Date;

  switch (preset) {
    case 'today':
      start = startOfDay(now);
      end = endOfDay(now);
      break;
    case 'month':
      start = startOfMonth(now);
      end = endOfMonth(now);
      break;
    case 'year':
      start = startOfYear(now);
      end = endOfYear(now);
      break;
    case 'custom': {
      start = startOfDay(new Date(`${customStart}T00:00:00`));
      end = endOfDay(new Date(`${customEnd}T00:00:00`));
      break;
    }
    case 'week':
    default:
      // Current week, Monday - Sunday
      start = startOfWeek(now, { weekStartsOn: 1 });
      end = endOfWeek(now, { weekStartsOn: 1 });
      break;
  }

  // Never query into the future
  return { start: start.getTime(), end: Math.min(end.getTime(), now.getTime()) };
};

const EmbedPhone: React.FC = () => {
  const [status, setStatus] = useState<{
    configured: boolean;
    target_type?: string;
    reason?: string;
    api_key_ref?: string | null;
  } | null>(null);
  const [error, setError] = useState<string | null>(null);

  const todayStr = format(new Date(), 'yyyy-MM-dd');
  const [preset, setPreset] = useState<RangePreset>('week');
  const [customStart, setCustomStart] = useState(todayStr);
  const [customEnd, setCustomEnd] = useState(todayStr);
  const [appliedRange, setAppliedRange] = useState<DateRange>(() =>
    computeRange('week', todayStr, todayStr),
  );

  useEffect(() => {
    const loadStatus = async () => {
      try {
        const data = await api.getEmbedPhoneStatus();
        setStatus(data);
      } catch (err: any) {
        setError(err.response?.data?.message || 'Failed to check Embed Call integration status');
      }
    };
    loadStatus();
  }, []);

  // Report data via React Query: cached per range for 60s so navigating or
  // toggling filters doesn't refetch. The Refresh button sets a ref that makes
  // the next fetch bypass the server cache and re-pull today's calls live.
  const forceRefreshRef = useRef(false);
  const {
    data: report,
    isFetching,
    error: reportError,
    refetch,
  } = useQuery<ReportData>({
    queryKey: ['embed-phone-report', appliedRange.start, appliedRange.end],
    queryFn: async () => {
      const force = forceRefreshRef.current;
      forceRefreshRef.current = false;
      const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      return api.getEmbedPhoneReport(appliedRange.start, appliedRange.end, timezone, force);
    },
    enabled: !!status?.configured,
    staleTime: 60 * 1000,
  });

  useEffect(() => {
    if (reportError) {
      setError((reportError as any)?.response?.data?.message || 'Failed to load call report');
    }
  }, [reportError]);

  useEffect(() => {
    if (report) setError(null);
  }, [report]);

  const handleRefresh = () => {
    forceRefreshRef.current = true;
    setError(null);
    refetch();
  };

  const handlePresetChange = (_: React.MouseEvent<HTMLElement>, value: RangePreset | null) => {
    if (!value) return;
    setPreset(value);
    if (value !== 'custom') {
      setAppliedRange(computeRange(value, customStart, customEnd));
    }
  };

  const handleApplyCustom = () => {
    if (!customStart || !customEnd) return;
    const range = computeRange('custom', customStart, customEnd);
    if (range.start >= range.end) {
      setError('Custom range start must be before the end date');
      return;
    }
    setAppliedRange(range);
  };


  // Fill every day in the range so charts show continuous series (zeros included)
  const dailySeries = useMemo<DailyPoint[]>(() => {
    if (!report) return [];
    const byDate = new Map(report.daily.map((d) => [d.date, d]));
    const days = eachDayOfInterval({
      start: appliedRange.start,
      end: Math.min(appliedRange.end, Date.now()),
    });
    return days.map((d) => {
      const key = format(d, 'yyyy-MM-dd');
      return byDate.get(key) || { date: key, total: 0, answered: 0, missed: 0, talkSeconds: 0 };
    });
  }, [report, appliedRange]);

  const dailyLabels = useMemo(
    () => dailySeries.map((d) => format(parseISO(d.date), 'EEE M/d')),
    [dailySeries],
  );

  const stackedBarOptions = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      plugins: { legend: { display: true, position: 'top' as const }, tooltip: { enabled: true } },
      scales: { x: { stacked: true }, y: { stacked: true, beginAtZero: true } },
    }),
    [],
  );

  const lineOptions = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      plugins: { legend: { display: false }, tooltip: { enabled: true } },
      scales: { y: { beginAtZero: true } },
    }),
    [],
  );

  const barOptions = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      plugins: { legend: { display: false }, tooltip: { enabled: true } },
      scales: { y: { beginAtZero: true } },
    }),
    [],
  );

  const doughnutOptions = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      plugins: { legend: { display: true, position: 'right' as const }, tooltip: { enabled: true } },
    }),
    [],
  );

  if (status && !status.configured) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        {status.reason === 'missing_api_key' && (
          <Alert severity="error">
            Embed Call is configured for this organization, but the backend server
            is missing the API key environment variable
            {status.api_key_ref ? ` (${status.api_key_ref})` : ''}. Add it to the
            backend service environment (Render &rarr; soniq-pulse-api &rarr;
            Environment) and the integration will activate on restart.
          </Alert>
        )}
        {status.reason === 'inactive' && (
          <Alert severity="warning">
            The Embed Call integration for this organization is disabled. A master
            admin can re-enable it under Module Permissions &rarr; Organizations
            &rarr; Integrations.
          </Alert>
        )}
        {(!status.reason || status.reason === 'not_configured') && (
          <Alert severity="warning">
            Embed Call integration is not configured for this organization. Ask a
            master admin to configure it under Module Permissions &rarr;
            Organizations &rarr; Integrations.
          </Alert>
        )}
      </Container>
    );
  }


  const totals = report?.totals;
  const highlights = report?.highlights;

  const kpiCards: {
    label: string;
    value: string;
    sub?: string;
    icon: React.ReactElement;
    gradient: string;
  }[] = totals
    ? [
        {
          label: 'Total Calls',
          value: totals.totalCalls.toLocaleString(),
          sub: `${totals.avgCallsPerDay}/day avg`,
          icon: <PhoneInTalkIcon sx={{ fontSize: 36 }} />,
          gradient: 'linear-gradient(135deg, #1e3c72 0%, #2a5298 100%)',
        },
        {
          label: 'Answered Calls',
          value: totals.answeredCalls.toLocaleString(),
          sub: `avg talk ${formatDuration(totals.avgTalkSeconds)} per call`,
          icon: <AnsweredIcon sx={{ fontSize: 36 }} />,
          gradient: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)',
        },
        {
          label: 'Call Backs',
          value: totals.missedCalls.toLocaleString(),
          sub: 'calls requiring followup',
          icon: <CallBackIcon sx={{ fontSize: 36 }} />,
          gradient: 'linear-gradient(135deg, #cb2d3e 0%, #ef473a 100%)',
        },
        {
          label: 'Total Talk Time',
          value: formatTalkHours(totals.totalTalkSeconds),
          sub: `${Math.round(totals.totalTalkSeconds / 60).toLocaleString()} minutes on the phone`,
          icon: <TalkTimeIcon sx={{ fontSize: 36 }} />,
          gradient: 'linear-gradient(135deg, #8e2de2 0%, #4a00e0 100%)',
        },
        {
          label: 'Avg Talk Time',
          value: formatDuration(totals.avgTalkSeconds),
          sub: 'per answered call',
          icon: <AvgTalkIcon sx={{ fontSize: 36 }} />,
          gradient: 'linear-gradient(135deg, #fc4a1a 0%, #f7b733 100%)',
        },
        {
          label: 'Inbound Calls',
          value: totals.inbound.toLocaleString(),
          sub: `${totals.outbound.toLocaleString()} outbound`,
          icon: <InboundIcon sx={{ fontSize: 36 }} />,
          gradient: 'linear-gradient(135deg, #0f9b0f 0%, #00b09b 100%)',
        },
        {
          label: 'Busiest Day',
          value: highlights?.busiestDay
            ? format(parseISO(highlights.busiestDay.date), 'EEE M/d')
            : '-',
          sub: highlights?.busiestDay ? `${highlights.busiestDay.total} calls handled` : undefined,
          icon: <BusiestDayIcon sx={{ fontSize: 36 }} />,
          gradient: 'linear-gradient(135deg, #c33764 0%, #1d2671 100%)',
        },
      ]
    : [];


  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      {/* HEADER */}
      <Box
        sx={{
          p: 4,
          mb: 3,
          background: 'linear-gradient(135deg, #2C3E50 0%, #4B88A2 100%)',
          color: 'white',
          borderRadius: 2,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
          <PhoneInTalkIcon sx={{ fontSize: 48 }} />
          <Box sx={{ flexGrow: 1 }}>
            <Typography variant="h4" fontWeight="600">
              Embed Call
            </Typography>
            <Typography variant="body1" sx={{ opacity: 0.9 }}>
              Call center performance reporting &mdash;{' '}
              {format(appliedRange.start, 'MMM d, yyyy')} to{' '}
              {format(appliedRange.end, 'MMM d, yyyy')}
              {report?.timezone ? ` (${report.timezone})` : ''}
            </Typography>
          </Box>
          <Button
            variant="outlined"
            onClick={handleRefresh}
            disabled={isFetching || !status?.configured}
            startIcon={isFetching ? <CircularProgress size={18} color="inherit" /> : <RefreshIcon />}
            sx={{ color: 'white', borderColor: 'rgba(255,255,255,0.6)' }}
          >
            Refresh
          </Button>
        </Box>
      </Box>

      {/* DATE RANGE FILTER */}
      <Paper elevation={2} sx={{ p: 2, mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
          <ToggleButtonGroup
            value={preset}
            exclusive
            onChange={handlePresetChange}
            size="small"
            color="primary"
          >
            <ToggleButton value="today">Today</ToggleButton>
            <ToggleButton value="week">This Week (Mon-Sun)</ToggleButton>
            <ToggleButton value="month">This Month</ToggleButton>
            <ToggleButton value="year">This Year</ToggleButton>
            <ToggleButton value="custom">Custom Range</ToggleButton>
          </ToggleButtonGroup>
          {preset === 'custom' && (
            <>
              <TextField
                type="date"
                label="Start"
                size="small"
                InputLabelProps={{ shrink: true }}
                value={customStart}
                onChange={(e) => setCustomStart(e.target.value)}
              />
              <TextField
                type="date"
                label="End"
                size="small"
                InputLabelProps={{ shrink: true }}
                value={customEnd}
                onChange={(e) => setCustomEnd(e.target.value)}
              />
              <Button variant="contained" onClick={handleApplyCustom} disabled={isFetching}>
                Apply
              </Button>
            </>
          )}
        </Box>
      </Paper>

      {isFetching && <LinearProgress sx={{ mb: 2, borderRadius: 1 }} />}

      {error && (
        <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {report?.truncated && (
        <Alert severity="info" sx={{ mb: 2 }}>
          This range contains a very high call volume - results reflect the calls retrieved
          before the page cap was reached. Narrow the date range for a fully exhaustive report.
        </Alert>
      )}


      {/* KPI CARDS */}
      {totals && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          {kpiCards.map((card) => (
            <Grid item xs={12} sm={6} md={3} key={card.label}>
              <Paper
                elevation={3}
                sx={{
                  p: 2.5,
                  height: '100%',
                  background: card.gradient,
                  color: 'white',
                  borderRadius: 2,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 2,
                }}
              >
                <Box sx={{ opacity: 0.9 }}>{card.icon}</Box>
                <Box>
                  <Typography
                    variant="caption"
                    sx={{ opacity: 0.85, textTransform: 'uppercase', letterSpacing: 0.5 }}
                  >
                    {card.label}
                  </Typography>
                  <Typography variant="h4" fontWeight="700" lineHeight={1.1}>
                    {card.value}
                  </Typography>
                  {card.sub && (
                    <Typography variant="caption" sx={{ opacity: 0.85 }}>
                      {card.sub}
                    </Typography>
                  )}
                </Box>
              </Paper>
            </Grid>
          ))}
        </Grid>
      )}


      {/* CHARTS ROW 1: daily volume + outcome split */}
      {report && (
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={12} md={8}>
            <Paper elevation={2} sx={{ p: 2, height: '100%' }}>
              <Typography variant="h6" gutterBottom>
                Daily Call Volume
              </Typography>
              <Box sx={{ height: 320 }}>
                <Bar
                  data={{
                    labels: dailyLabels,
                    datasets: [
                      {
                        label: 'Answered',
                        data: dailySeries.map((d) => d.answered),
                        backgroundColor: '#4caf50',
                      },
                      {
                        label: 'Call Backs',
                        data: dailySeries.map((d) => d.missed),
                        backgroundColor: '#ef5350',
                      },
                    ],
                  }}
                  options={stackedBarOptions}
                />
              </Box>
            </Paper>
          </Grid>
          <Grid item xs={12} md={4}>
            <Paper elevation={2} sx={{ p: 2, height: '100%' }}>
              <Typography variant="h6" gutterBottom>
                Call Outcome
              </Typography>
              <Box sx={{ height: 320 }}>
                <Doughnut
                  data={{
                    labels: ['Answered', 'Call Backs'],
                    datasets: [
                      {
                        data: [report.totals.answeredCalls, report.totals.missedCalls],
                        backgroundColor: ['#4caf50', '#ef5350'],
                        borderWidth: 2,
                      },
                    ],
                  }}
                  options={doughnutOptions}
                />
              </Box>
            </Paper>
          </Grid>
        </Grid>
      )}


      {/* CHARTS ROW 2: talk time trend + hourly distribution */}
      {report && (
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={12} md={6}>
            <Paper elevation={2} sx={{ p: 2, height: '100%' }}>
              <Typography variant="h6" gutterBottom>
                Talk Minutes per Day
              </Typography>
              <Box sx={{ height: 300 }}>
                <Line
                  data={{
                    labels: dailyLabels,
                    datasets: [
                      {
                        label: 'Talk Minutes',
                        data: dailySeries.map((d) => Math.round((d.talkSeconds / 60) * 10) / 10),
                        borderColor: '#7b1fa2',
                        backgroundColor: 'rgba(123, 31, 162, 0.15)',
                        fill: true,
                        tension: 0.3,
                      },
                    ],
                  }}
                  options={lineOptions}
                />
              </Box>
            </Paper>
          </Grid>
          <Grid item xs={12} md={6}>
            <Paper elevation={2} sx={{ p: 2, height: '100%' }}>
              <Typography variant="h6" gutterBottom>
                Calls by Hour of Day
              </Typography>
              <Box sx={{ height: 300 }}>
                <Bar
                  data={{
                    labels: report.hourly.map((h) => formatHour(h.hour)),
                    datasets: [
                      {
                        label: 'Calls',
                        data: report.hourly.map((h) => h.total),
                        backgroundColor: report.hourly.map((h) =>
                          highlights?.peakHour && h.hour === highlights.peakHour.hour
                            ? '#ff9800'
                            : '#26a69a',
                        ),
                      },
                    ],
                  }}
                  options={barOptions}
                />
              </Box>
            </Paper>
          </Grid>
        </Grid>
      )}


      {/* CHARTS ROW 3: day-of-week + highlights */}
      {report && (
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={12} md={6}>
            <Paper elevation={2} sx={{ p: 2, height: '100%' }}>
              <Typography variant="h6" gutterBottom>
                Calls by Day of Week
              </Typography>
              <Box sx={{ height: 300 }}>
                <Bar
                  data={{
                    labels: report.byDayOfWeek.map((d) => d.day),
                    datasets: [
                      {
                        label: 'Calls',
                        data: report.byDayOfWeek.map((d) => d.total),
                        backgroundColor: '#5c6bc0',
                      },
                    ],
                  }}
                  options={barOptions}
                />
              </Box>
            </Paper>
          </Grid>
          <Grid item xs={12} md={6}>
            <Paper elevation={2} sx={{ p: 3, height: '100%' }}>
              <Typography variant="h6" gutterBottom>
                Highlights
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
                {highlights?.longestCall && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <TrophyIcon color="primary" />
                    <Typography variant="body1">
                      Longest call: <strong>{formatDuration(highlights.longestCall.duration_seconds)}</strong>
                      {highlights.longestCall.agent_name
                        ? ` by ${highlights.longestCall.agent_name}`
                        : ''}
                      {highlights.longestCall.contact_name
                        ? ` with ${highlights.longestCall.contact_name}`
                        : ''}
                    </Typography>
                  </Box>
                )}
                {highlights?.peakHour && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <PeakHourIcon color="secondary" />
                    <Typography variant="body1">
                      Peak hour: <strong>{formatHour(highlights.peakHour.hour)}</strong> with{' '}
                      {highlights.peakHour.total} calls
                    </Typography>
                  </Box>
                )}
                {highlights?.busiestDay && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <BusiestDayIcon color="success" />
                    <Typography variant="body1">
                      Busiest day:{' '}
                      <strong>{format(parseISO(highlights.busiestDay.date), 'EEEE, MMM d')}</strong>{' '}
                      with {highlights.busiestDay.total} calls
                    </Typography>
                  </Box>
                )}
                {report.totals.totalCalls === 0 && (
                  <Typography variant="body2" color="text.secondary">
                    No calls in this date range yet. Pick a wider range or check back later.
                  </Typography>
                )}
              </Box>
            </Paper>
          </Grid>
        </Grid>
      )}

      {!isFetching && report && report.totals.totalCalls === 0 && (
        <Alert severity="info">
          No calls found in the selected range. Try a different range.
        </Alert>
      )}
    </Container>
  );
};

export default EmbedPhone;
